// RecentSessionsStrip — fast-resume horizontal scroller of the coach's last
// N intelligence sessions. Moved verbatim from DecisionEnginePage.js during
// Phase 2 of the System Consolidation. Refetches whenever `refreshKey`
// bumps (after each successful /ask). Clicking a card asks the parent to
// resume the session.
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Pencil, Pin, PinOff } from 'lucide-react';

import { API, relativeTime } from './helpers';

export const RecentSessionsStrip = ({ refreshKey, currentSessionId, onResume }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [savedPulse, setSavedPulse] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/intelligence/sessions`, {
          params: { mine: true, limit: 5 },
        });
        if (alive) setItems(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        if (alive) setItems([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [refreshKey]);

  const beginEdit = (s, e) => {
    e?.stopPropagation();
    setEditingId(s.session_id);
    setEditDraft(s.title || '');
  };

  const sortItems = (xs) =>
    [...xs].sort((a, b) => {
      const pa = a.pinned ? 1 : 0;
      const pb = b.pinned ? 1 : 0;
      if (pa !== pb) return pb - pa;  // pinned first
      return (b.updated_at || '').localeCompare(a.updated_at || '');  // newer first
    });

  const togglePin = async (s, e) => {
    e?.stopPropagation();
    const next = !s.pinned;
    // Optimistic update + immediate re-sort.
    setItems((xs) => sortItems(xs.map((x) => (x.session_id === s.session_id ? { ...x, pinned: next } : x))));
    try {
      const r = await axios.patch(`${API}/intelligence/sessions/${s.session_id}/pin`, {
        pinned: next,
      });
      setItems((xs) => sortItems(xs.map((x) => (x.session_id === s.session_id ? r.data : x))));
    } catch (err) {
      // Roll back optimistic change.
      setItems((xs) => sortItems(xs.map((x) => (x.session_id === s.session_id ? { ...x, pinned: !next } : x))));
      toast.error(err.response?.data?.detail || 'Could not update pin');
    }
  };

  const saveTitle = async (s) => {
    const next = editDraft.trim();
    const prev = s.title || '';
    if (next === prev) {
      setEditingId(null);
      return;
    }
    // Optimistic update.
    setItems((xs) => xs.map((x) => (x.session_id === s.session_id ? { ...x, title: next || null } : x)));
    setEditingId(null);
    try {
      const r = await axios.patch(`${API}/intelligence/sessions/${s.session_id}`, {
        title: next || null,
      });
      setItems((xs) => xs.map((x) => (x.session_id === s.session_id ? r.data : x)));
      setSavedPulse(s.session_id);
      setTimeout(() => setSavedPulse((id) => (id === s.session_id ? null : id)), 1500);
    } catch (e) {
      // Roll back to the server-source-of-truth by refetching.
      try {
        const r = await axios.get(`${API}/intelligence/sessions`, { params: { mine: true, limit: 5 } });
        setItems(Array.isArray(r.data) ? r.data : []);
      } catch {}
      toast.error(e.response?.data?.detail || 'Could not save title');
    }
  };

  const onEditKeyDown = (ev, s) => {
    if (ev.key === 'Enter') {
      ev.preventDefault();
      saveTitle(s);
    } else if (ev.key === 'Escape') {
      ev.preventDefault();
      setEditingId(null);
    }
  };

  if (loading && items.length === 0) {
    // Non-blocking — render nothing while the first fetch is in-flight.
    return null;
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="recent-sessions-strip">
      <div className="flex items-center justify-between mb-1.5">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Recent Sessions</h3>
        <span className="text-[10px] text-gray-400">last {Math.min(5, items.length) || 0}</span>
      </div>
      {items.length === 0 ? (
        <div className="text-[11px] text-gray-400 italic px-1 py-2" data-testid="recent-sessions-empty">
          No recent sessions yet.
        </div>
      ) : (
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="recent-sessions-list">
          {items.map((s) => {
            const isActive = s.session_id === currentSessionId;
            const isEditing = editingId === s.session_id;
            const label = (s.title && s.title.trim()) || s.context_summary || '';
            return (
              <div
                key={s.session_id}
                role="button"
                tabIndex={0}
                onClick={() => !isEditing && onResume(s.session_id)}
                onKeyDown={(e) => !isEditing && e.key === 'Enter' && onResume(s.session_id)}
                className={`shrink-0 w-56 text-left rounded-md border px-3 py-2 transition-colors cursor-pointer ${
                  isActive
                    ? 'border-indigo-400 bg-indigo-50/60 dark:border-indigo-800 dark:bg-indigo-950/40'
                    : 'border-gray-100 dark:border-gray-800 hover:border-indigo-300 dark:hover:border-indigo-700 hover:bg-indigo-50/40 dark:hover:bg-indigo-950/20'
                }`}
                data-testid={`recent-session-card-${s.session_id}`}
                title={s.session_id}
              >
                <div className="flex items-center justify-between gap-1 mb-0.5">
                  <span className="text-[10px] text-gray-500">{relativeTime(s.updated_at)}</span>
                  <div className="flex items-center gap-1">
                    {savedPulse === s.session_id && (
                      <span className="text-[10px] text-emerald-600 inline-flex items-center gap-0.5" data-testid={`session-saved-${s.session_id}`}>
                        <Check className="h-2.5 w-2.5" /> Saved
                      </span>
                    )}
                    {isActive && !isEditing && (
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" title="current" />
                    )}
                    {!isEditing && (
                      <button
                        type="button"
                        onClick={(e) => togglePin(s, e)}
                        className={`p-0.5 rounded ${s.pinned ? 'text-indigo-600 hover:text-indigo-700' : 'text-gray-400 hover:text-indigo-600'}`}
                        title={s.pinned ? 'Unpin' : 'Pin to top'}
                        data-testid={`session-pin-btn-${s.session_id}`}
                        data-pinned={s.pinned ? 'true' : 'false'}
                      >
                        {s.pinned ? (
                          <Pin className="h-3 w-3 fill-indigo-600" />
                        ) : (
                          <PinOff className="h-3 w-3" />
                        )}
                      </button>
                    )}
                    {!isEditing && (
                      <button
                        type="button"
                        onClick={(e) => beginEdit(s, e)}
                        className="text-gray-400 hover:text-indigo-600 p-0.5 rounded"
                        title="Rename"
                        data-testid={`session-rename-btn-${s.session_id}`}
                      >
                        <Pencil className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
                {isEditing ? (
                  <input
                    autoFocus
                    type="text"
                    value={editDraft}
                    maxLength={80}
                    onChange={(e) => setEditDraft(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onBlur={() => saveTitle(s)}
                    onKeyDown={(e) => onEditKeyDown(e, s)}
                    placeholder="Session title…"
                    className="w-full text-xs bg-white dark:bg-gray-900 border border-indigo-300 dark:border-indigo-700 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                    data-testid={`session-title-input-${s.session_id}`}
                  />
                ) : (
                  <div
                    className={`text-xs line-clamp-2 ${s.title ? 'text-gray-900 dark:text-gray-100 font-medium' : 'text-gray-800 dark:text-gray-200'}`}
                    data-testid={`recent-session-summary-${s.session_id}`}
                  >
                    {label || <span className="italic text-gray-400">(no summary)</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
